import type { MatchEvent } from "@arcade-hockey/shared";
import { sweptCircleVsSegment } from "../math/Geometry.js";
import type { Vec2 } from "../math/Vec2.js";
import { tuning } from "../state/tuning.js";
import type { PuckState } from "../state/types.js";

const RESTITUTION = 0.85;
const TANGENT_DAMPING = 0.97;
const MAX_BOUNCES = 4;

type Segment = { a: Vec2; b: Vec2 };

// 12 chord segments per quarter-arc → ~4 cm chord deviation against the true
// 5 m corner radius. Fine for a 22 cm puck and well below the per-tick step
// at every plausible speed.
const ARC_CHORDS_PER_CORNER = 12;

function rinkSegments(): Segment[] {
  const halfL = tuning.rink.length / 2;
  const halfW = tuning.rink.width / 2;
  const corner = tuning.rink.cornerRadius;
  const segments: Segment[] = [
    // Straight edges (shortened to corner inset).
    { a: { x: -halfL + corner, y: -halfW }, b: { x: halfL - corner, y: -halfW } }, // bottom
    { a: { x: halfL, y: -halfW + corner }, b: { x: halfL, y: halfW - corner } }, // right
    { a: { x: halfL - corner, y: halfW }, b: { x: -halfL + corner, y: halfW } }, // top
    { a: { x: -halfL, y: halfW - corner }, b: { x: -halfL, y: -halfW + corner } }, // left
  ];

  // Corner arcs as chord polylines. Angle 0 = +x; +π/2 = +y.
  const corners: ReadonlyArray<{ cx: number; cy: number; start: number }> = [
    { cx: halfL - corner, cy: halfW - corner, start: 0 }, // top-right
    { cx: -halfL + corner, cy: halfW - corner, start: Math.PI / 2 }, // top-left
    { cx: -halfL + corner, cy: -halfW + corner, start: Math.PI }, // bottom-left
    { cx: halfL - corner, cy: -halfW + corner, start: (3 * Math.PI) / 2 }, // bottom-right
  ];
  const da = Math.PI / 2 / ARC_CHORDS_PER_CORNER;
  for (const c of corners) {
    let prevX = c.cx + Math.cos(c.start) * corner;
    let prevY = c.cy + Math.sin(c.start) * corner;
    for (let i = 1; i <= ARC_CHORDS_PER_CORNER; i++) {
      const angle = c.start + da * i;
      const nx = c.cx + Math.cos(angle) * corner;
      const ny = c.cy + Math.sin(angle) * corner;
      segments.push({ a: { x: prevX, y: prevY }, b: { x: nx, y: ny } });
      prevX = nx;
      prevY = ny;
    }
  }

  // Goal frames — three sides of a U, mouth open toward the rink. Shape
  // matches the skater-vs-goal box in rink-collision.ts so the puck can't
  // pass through the back of the net (e.g. shooting from behind) or
  // through the side-of-net. Pucks crossing the open mouth side count as
  // goals via tickGoalDetection.
  //
  // The endpoints at (lineX, ±halfMouth) act as the goal posts — a puck
  // approaching the post along y = ±halfMouth bounces off the segment's
  // start point ("rang off the post").
  const halfMouth = tuning.rink.goalMouthHalfWidth;
  const goalDepth = tuning.rink.goalDepth;
  const awayLineX = halfL - tuning.rink.goalLineOffset;
  const awayBackX = awayLineX + goalDepth;
  const homeLineX = -halfL + tuning.rink.goalLineOffset;
  const homeBackX = homeLineX - goalDepth;
  // Away goal (x = +awayLineX, opens toward -x).
  segments.push({
    a: { x: awayLineX, y: halfMouth },
    b: { x: awayBackX, y: halfMouth },
  }); // top side
  segments.push({
    a: { x: awayBackX, y: halfMouth },
    b: { x: awayBackX, y: -halfMouth },
  }); // back
  segments.push({
    a: { x: awayBackX, y: -halfMouth },
    b: { x: awayLineX, y: -halfMouth },
  }); // bottom side
  // Home goal (x = -|homeLineX|, opens toward +x).
  segments.push({
    a: { x: homeLineX, y: halfMouth },
    b: { x: homeBackX, y: halfMouth },
  }); // top side
  segments.push({
    a: { x: homeBackX, y: halfMouth },
    b: { x: homeBackX, y: -halfMouth },
  }); // back
  segments.push({
    a: { x: homeBackX, y: -halfMouth },
    b: { x: homeLineX, y: -halfMouth },
  }); // bottom side

  return segments;
}

const SEGMENTS = rinkSegments();

// Scratch buffer for the swept-collision target endpoint. The bounce loop is
// strictly synchronous; reusing this avoids a per-iteration Vec2 allocation.
const TARGET_SCRATCH: Vec2 = { x: 0, y: 0 };

/**
 * Integrate the loose puck for `dt` seconds, emitting board-hit events into
 * `events` (typically `state.events`). Mutates `puck` in place. Returns void
 * — appending directly to the caller's event array avoids a per-tick array
 * allocation in the hot path.
 */
export function integrateLoosePuck(
  puck: PuckState,
  dt: number,
  currentTick: number,
  events: MatchEvent[],
): void {
  const t = tuning.puck;

  // Apply drag (in-place).
  let dragFactor = t.drag;
  const speed = Math.sqrt(puck.vel.x * puck.vel.x + puck.vel.y * puck.vel.y);
  if (speed < t.lowSpeedThreshold) {
    dragFactor *= t.lowSpeedDrag;
  }
  puck.vel.x *= dragFactor;
  puck.vel.y *= dragFactor;

  // Move with swept collision, up to MAX_BOUNCES iterations per tick.
  // We track the best (smallest-toi) hit across the four segments as scalars,
  // since sweptCircleVsSegment returns a shared module-scope scratch that is
  // overwritten on each call.
  let remaining = dt;
  let bounces = 0;
  while (remaining > 0 && bounces < MAX_BOUNCES) {
    const stepVelX = puck.vel.x * remaining;
    const stepVelY = puck.vel.y * remaining;
    TARGET_SCRATCH.x = puck.pos.x + stepVelX;
    TARGET_SCRATCH.y = puck.pos.y + stepVelY;

    let hasHit = false;
    let bestToi = Number.POSITIVE_INFINITY;
    let bestPx = 0;
    let bestPy = 0;
    let bestNx = 0;
    let bestNy = 0;
    for (const seg of SEGMENTS) {
      const hit = sweptCircleVsSegment(puck.pos, TARGET_SCRATCH, t.radius, seg.a, seg.b);
      if (hit !== null && hit.toi < bestToi) {
        hasHit = true;
        bestToi = hit.toi;
        bestPx = hit.point.x;
        bestPy = hit.point.y;
        bestNx = hit.normal.x;
        bestNy = hit.normal.y;
      }
    }

    if (!hasHit) {
      puck.pos.x = TARGET_SCRATCH.x;
      puck.pos.y = TARGET_SCRATCH.y;
      remaining = 0;
      break;
    }

    // Advance to just before the hit (in-place).
    const safeT = Math.max(0, bestToi - 1e-6);
    puck.pos.x += stepVelX * safeT;
    puck.pos.y += stepVelY * safeT;

    // Reflect velocity.
    const vn = puck.vel.x * bestNx + puck.vel.y * bestNy;
    if (vn < 0) {
      // velocity has component into the wall; reflect with restitution.
      const reflectedX = puck.vel.x - bestNx * vn * (1 + RESTITUTION);
      const reflectedY = puck.vel.y - bestNy * vn * (1 + RESTITUTION);
      // Apply tangential damping.
      const reflectedDotN = reflectedX * bestNx + reflectedY * bestNy;
      const tangentialX = reflectedX - bestNx * reflectedDotN;
      const tangentialY = reflectedY - bestNy * reflectedDotN;
      const normalX = bestNx * reflectedDotN;
      const normalY = bestNy * reflectedDotN;
      puck.vel.x = normalX + tangentialX * TANGENT_DAMPING;
      puck.vel.y = normalY + tangentialY * TANGENT_DAMPING;
      events.push({
        type: "PuckBoardHit",
        tick: currentTick,
        pos: { x: bestPx, y: bestPy },
        speed: Math.sqrt(puck.vel.x * puck.vel.x + puck.vel.y * puck.vel.y),
        material: "Board",
      });
    }

    remaining *= 1 - safeT;
    bounces += 1;
  }

  if (bounces >= MAX_BOUNCES) {
    // Worst case: damp velocity to avoid runaway bounces.
    puck.vel.x *= 0.5;
    puck.vel.y *= 0.5;
  }
}
